// ============================================
// Portal Login Page — UKA-X01 D2
//
// Password-only access gate for the Expert Clinical Review Portal.
// Mobile-first, iPad Safari primary target.
// No framer-motion. No email. Password only.
// ============================================

import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { verifyPassword, createSession, isAuthenticated, ensureFirebaseAuth } from '../lib/auth';

// ── Types ─────────────────────────────────────────────────────────

interface LocationState {
  from?: { pathname: string };
}

// ── Component ─────────────────────────────────────────────────────

export function LoginPage() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const inputRef   = useRef<HTMLInputElement>(null);

  const [password, setPassword]   = useState('');
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);

  const locationState = location.state as LocationState | null;
  const from = locationState?.from?.pathname ?? '/portal/dashboard';

  // Already authenticated — redirect immediately
  useEffect(() => {
    if (isAuthenticated()) {
      navigate(from, { replace: true });
    }
  }, [from, navigate]);

  // Auto-focus password field on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Small delay so the loading state renders before the check
    setTimeout(async () => {
      if (verifyPassword(password)) {
        createSession();
        await ensureFirebaseAuth();
        navigate(from, { replace: true });
      } else {
        setError('Incorrect password. Please try again.');
        setPassword('');
        setLoading(false);
        inputRef.current?.focus();
      }
    }, 300);
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#FAFAFA',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        fontFamily: 'DM Sans, system-ui, sans-serif',
      }}
    >
      <div style={{ width: '100%', maxWidth: '400px' }}>

        {/* Branding */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: 'rgba(225,29,72,0.1)',
              marginBottom: '16px',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E11D48" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <h1
            style={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#18181B',
              margin: '0 0 6px',
              fontFamily: 'Instrument Serif, Georgia, serif',
            }}
          >
            Expert Review Portal
          </h1>
          <p style={{ fontSize: '14px', color: '#71717A', margin: 0 }}>
            HemoLogix Clinical Calibration
          </p>
        </div>

        {/* Login Card */}
        <div
          style={{
            background: '#FFFFFF',
            border: '1px solid #E4E4E7',
            borderRadius: '8px',
            padding: '28px 24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}
        >
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label
                htmlFor="portal-password"
                style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#52525B',
                  marginBottom: '6px',
                }}
              >
                Access Password
              </label>
              <input
                ref={inputRef}
                id="portal-password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError('');
                }}
                placeholder="Enter portal password"
                disabled={loading}
                autoComplete="current-password"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '15px',
                  border: error ? '1px solid #E11D48' : '1px solid #E4E4E7',
                  borderRadius: '4px',
                  background: '#F8FAFC',
                  color: '#18181B',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s',
                }}
              />
            </div>

            {/* Error */}
            {error && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: '#FEF2F2',
                  border: '1px solid #FEE2E2',
                  borderRadius: '4px',
                  padding: '10px 12px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  color: '#DC2626',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !password}
              style={{
                width: '100%',
                padding: '11px',
                fontSize: '15px',
                fontWeight: 600,
                background: loading || !password ? '#F4F4F5' : '#E11D48',
                color: loading || !password ? '#A1A1AA' : '#FFFFFF',
                border: 'none',
                borderRadius: '4px',
                cursor: loading || !password ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'background 0.15s',
                fontFamily: 'DM Sans, system-ui, sans-serif',
              }}
            >
              {loading ? (
                <>
                  <svg
                    width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    style={{ animation: 'portal-login-spin 0.8s linear infinite' }}
                  >
                    <line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/>
                    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
                    <line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/>
                    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
                  </svg>
                  Verifying...
                </>
              ) : (
                'Enter Portal'
              )}
            </button>
          </form>
        </div>

        <p style={{ textAlign: 'center', fontSize: '12px', color: '#A1A1AA', marginTop: '20px' }}>
          HemoLogix Health LLC — Expert Clinical Review
        </p>
      </div>

      <style>{`
        @keyframes portal-login-spin { to { transform: rotate(360deg); } }
        #portal-password:focus { border-color: #E11D48; box-shadow: 0 0 0 3px rgba(225,29,72,0.12); }
      `}</style>
    </div>
  );
}
